import { useCallback } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { merge } from 'lodash-es';

import { ICartEntry, ISanityImage } from '@rbi-ctg/menu';
import { PriceOrderDocument } from 'generated/graphql-gateway';
import { DeliveryStatus } from 'generated/rbi-graphql';
import { useOrderStatus } from 'hooks/order-status';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { IPriceOrderMutationResponse, IPriceOrderMutationVariables } from 'remote/queries/order';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOffersContext } from 'state/offers';
import { OrderStatus, useOrderContext } from 'state/order';
import { useUnavailableCartEntries } from 'state/order/hooks/use-unavailable-cart-entries';
import { buildPriceDeliveryInput, buildPriceOrderInput } from 'utils/cart';
import { computeOtherDiscountAmount } from 'utils/cart/helper';
import { isDelivery } from 'utils/service-mode';

import { ICatering, IPriceOrderParams, IUsePriceOrderParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1000;

type MutationResponse = IPriceOrderMutationResponse;
type MutationVariables = IPriceOrderMutationVariables;

export const sanitizeSanityImage = (image: ISanityImage) => {
  // We're knowingly breaking the contract of ICartEntry and ISanityImage here
  // we can remove these once we update @rbi-ctg/menu ICartEntry and ISanityImage
  // to make these properties options
  // @ts-ignore
  delete image?.hotspot;
  // @ts-ignore
  delete image?.crop;
  // @ts-ignore
  delete image?.asset?.metadata;
};

export const sanitizeCart = (cartEntries: ICartEntry[]): ICartEntry[] => {
  cartEntries.forEach(cartEntry => {
    if (!cartEntry.children.length && cartEntry.image?.asset?.metadata) {
      // @ts-ignore
      sanitizeSanityImage(cartEntry.image);

      return;
    }

    if (cartEntry.image) {
      sanitizeSanityImage(cartEntry.image);
    }

    sanitizeCart(cartEntry.children);
  });

  return cartEntries;
};

export const usePriceOrder = ({
  cartEntries,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
}: IUsePriceOrderParams) => {
  const offers = useOffersContext();
  const { updateTipAmount, calculateCartTotalWithDiscount, quoteId } = useOrderContext();
  const isSignUpAfterCart = useIsSignUpAfterCart();
  const [fireFulfillmentMutation, fulfillmentMutationResult] = useMutation<
    MutationResponse,
    MutationVariables
  >(PriceOrderDocument, { context: { shouldNotBatch: true } });
  const enablePaymentErrorContinue = useFlag(LaunchDarklyFlag.ENABLE_PAYMENT_ERROR_CONTINUE);
  const hideTipAmountCheckout = useFlag(LaunchDarklyFlag.HIDE_TIP_AMOUNT_CHECKOUT);

  const rbiOrderId = fulfillmentMutationResult?.data?.priceOrder?.rbiOrderId || null;

  const successStatuses = enablePaymentErrorContinue
    ? [OrderStatus.PRICE_SUCCESSFUL, OrderStatus.PAYMENT_ERROR, OrderStatus.VALIDATION_ERROR]
    : [OrderStatus.PRICE_SUCCESSFUL, OrderStatus.VALIDATION_ERROR];

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount?.() || {};

  const queryResult = useOrderStatus({
    failureStatuses: {
      delivery: [DeliveryStatus.QUOTE_ERROR, DeliveryStatus.QUOTE_UNAVAILABLE],
      pos: OrderStatus.PRICE_ERROR,
      catering: OrderStatus.PRICE_ERROR,
    },
    onSuccess(rbiOrder) {
      const { selectedOffer } = offers;

      if (selectedOffer) {
        offers.validateOfferRedeemable({
          selectedOffer,
          rbiOrderId: rbiOrder.rbiOrderId,
        });
      }

      if (isDelivery(rbiOrder.cart.serviceMode)) {
        const otherDiscountAmount = computeOtherDiscountAmount(rbiOrder.cart.discounts);
        updateTipAmount({
          subTotalCents: hideTipAmountCheckout ? 0 : rbiOrder.cart.subTotalCents,
          otherDiscountAmount,
        });
      }
    },
    orderStatusPollInterval,
    rbiOrderId,
    successStatuses: {
      delivery: DeliveryStatus.QUOTE_SUCCESSFUL,
      pos: successStatuses,
      catering: successStatuses,
    },
    skip: isCartTotalNegative,
  });

  const { orderStatus, serverOrder } = queryResult;

  const priceOrder = useCallback(
    (
      options: Omit<IPriceOrderParams, 'cartEntries' | 'redeemReward'>,
      user: UserDetails,
      catering?: ICatering
    ) => {
      if (!cartEntries.length || isSignUpAfterCart || isCartTotalNegative) {
        return;
      }

      const buildPriceOrderArgs = merge({ cartEntries: sanitizeCart(cartEntries) }, options);
      if (!buildPriceOrderArgs.isStoreOpenAndAvailable) {
        return;
      }

      const delivery = buildPriceDeliveryInput(options, user, quoteId);
      const input = buildPriceOrderInput(buildPriceOrderArgs);

      let variables: IPriceOrderMutationVariables = { delivery, input };

      if (catering) {
        variables = { ...variables, catering };
      }

      return fireFulfillmentMutation({ variables });
    },
    [cartEntries, isSignUpAfterCart, isCartTotalNegative, quoteId, fireFulfillmentMutation]
  );

  const { unavailableCartEntries } = useUnavailableCartEntries({
    cartEntries,
    serverOrder,
    interval: orderStatusPollInterval,
  });

  return {
    loading: queryResult.loading || fulfillmentMutationResult.loading,
    order: queryResult,
    orderStatus,
    priceOrder,
    priceOrderFailure: queryResult.failure,
    priceOrderSuccess: queryResult.success,
    priceResult: fulfillmentMutationResult,
    rbiOrderId,
    serverOrder,
    unavailableCartEntries,
  };
};
